<template>
  <div class="section recommendations-section">
    <div class="recommendations-container">
      <div class="section-header" :class="{ collapsed: isCollapsed }">
        <div class="section-title recommendations-title">
          <svg
            class="lightning-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
          </svg>
          Recommendations for You
        </div>
        <div class="header-actions">
          <button class="refresh-btn" @click="$emit('refresh')" v-if="!isCollapsed">
            <ArrowPathIcon class="w-4 h-4" />
            Refresh
          </button>
          <button
            @click="toggleCollapse"
            class="collapse-toggle"
            :title="isCollapsed ? 'Expand recommendations' : 'Collapse recommendations'"
          >
            <ChevronDownIcon
              class="w-5 h-5 transition-transform duration-300"
              :class="{ 'rotate-180': isCollapsed }"
            />
          </button>
        </div>
      </div>

      <!-- Collapsible Content -->
      <Transition name="collapse">
        <div v-show="!isCollapsed" class="recommendations-content">
          <!-- Loading State -->
          <div v-if="isLoading" class="tutors-grid recommendations-grid">
            <div v-for="i in 2" :key="'loading-' + i" class="tutor-card animate-pulse">
              <div class="tutor-card-header">
                <div class="tutor-avatar skeleton"></div>
                <div class="tutor-name-rating">
                  <div class="skeleton-line"></div>
                  <div class="skeleton-line short"></div>
                </div>
              </div>
              <div class="tutor-info">
                <div class="skeleton-line"></div>
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
              </div>
            </div>
          </div>

          <!-- No Recommendations State -->
          <div v-else-if="tutors.length === 0" class="text-center py-8">
            <div
              class="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4"
            >
              <svg
                class="w-8 h-8 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No Recommendations Available</h3>
            <p class="text-gray-500 max-w-md mx-auto">
              We're working on finding the perfect tutors for you. Check back soon!
            </p>
          </div>

          <!-- Recommendations Grid -->
          <div v-else class="tutors-grid recommendations-grid">
            <TutorCard
              v-for="tutor in tutors"
              :key="tutor.id"
              :tutor="tutor"
              :canBookSessions="canBookSessions"
              :canMessage="canMessage"
              :hasBlockingPendingPostTest="hasBlockingPendingPostTest"
              :isRecommended="true"
              @openProfile="$emit('openProfile', tutor)"
              @sendMessage="$emit('sendMessage', tutor)"
              @bookSession="$emit('bookSession', tutor)"
            />
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import TutorCard from "./TutorCard.vue";
import { ArrowPathIcon, ChevronDownIcon } from "@heroicons/vue/24/outline";

defineProps({
  tutors: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  canBookSessions: {
    type: Boolean,
    default: true,
  },
  canMessage: {
    type: Boolean,
    default: true,
  },
  hasBlockingPendingPostTest: {
    type: Boolean,
    default: true,
  },
});

defineEmits(["openProfile", "sendMessage", "bookSession", "refresh"]);

// Load saved collapse state immediately during initialization
const loadCollapsedState = () => {
  const savedState = localStorage.getItem("tutorRecommendationsCollapsed");
  return savedState === "true";
};

// Collapse state - load from localStorage immediately to prevent animation flicker
const isCollapsed = ref(loadCollapsedState());

// Toggle collapse and save state
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
  localStorage.setItem("tutorRecommendationsCollapsed", isCollapsed.value.toString());
};

// Method to collapse recommendations (callable from parent)
const collapseRecommendations = () => {
  if (!isCollapsed.value) {
    isCollapsed.value = true;
    localStorage.setItem("tutorRecommendationsCollapsed", "true");
  }
};

// Expose methods to parent component
defineExpose({
  collapseRecommendations
});
</script>

<style scoped>
.section {
  max-width: 1150px;
  margin: 0 auto 32px;
  padding: 0 20px;
  width: 100%;
}

.recommendations-section {
  margin-bottom: 50px;
  padding: 0 20px;
  width: 100%;
  overflow: visible;
}

.recommendations-container {
  background: #fdfdfd;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  padding-bottom: 40px;
  transition: transform 0.2s;
  margin: 0 auto;
  width: 100%;
  border: 3px solid #000000;
  border-radius: 12px;
  box-shadow: 2px 2px 0px #000;
  overflow: visible;
}

.recommendations-container:hover {
  transform: translateY(-1px);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  width: 100%;
}

.section-header.collapsed {
  margin-bottom: 0;
  transition: margin-bottom 0.3s ease;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.collapse-toggle {
  background: #fafafa;
  border: 2px solid #193cb8;
  box-shadow: 2px 2px 0px #193cb8;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #193cb8;
  border-radius: 50%;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.collapse-toggle:hover {
  background: #193cb8;
  color: white;
  transform: scale(1.05);
}

.collapse-toggle svg {
  transition: transform 0.3s ease;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #193cb8;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.tutor-count {
  font-size: 16px;
  font-weight: 600;
  color: #666;
  margin-left: 4px;
}

.recommendations-title {
  font-size: 22px !important;
  font-weight: 800 !important;
  color: #193cb8 !important;
}

.lightning-icon {
  color: #193cb8;
}

.refresh-btn {
  padding: 6px 12px;
  background: white;
  border: 2px solid #016630;
  border-radius: 12px;
  color: #016630;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  box-shadow: 2px 2px 0px #016630;
}

.refresh-btn:hover {
  transform: translateY(-1px);
  box-shadow: 3px 3px 0px #016630;
  color: #016630;
}

.refresh-icon {
  color: currentColor;
  stroke-width: 2.5;
}

.tutors-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 16px;
  width: 100%;
  overflow: visible !important;
}

/* Loading States */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.skeleton {
  background-color: #e2e8f0;
}

.skeleton-line {
  height: 16px;
  background-color: #e2e8f0;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-line.short {
  width: 60%;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .tutors-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .section-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}

/* Collapse Transition - Smooth animation without layout shift */
.collapse-enter-active {
  animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.collapse-leave-active {
  animation: slideOut 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
}

.recommendations-content {
  transform-origin: top center;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  overflow: visible;
}

.tutors-grid {
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}
</style>
